import { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { t } from '@/lib/i18n';
import { api } from '@/lib/api';

const LangCtx = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem('gm_lang') || 'en');

  const setLang = useCallback((l) => {
    localStorage.setItem('gm_lang', l);
    setLangState(l);
    // Persist to backend so AI mission generation + coach replies use the new
    // language immediately. Fire-and-forget; ignore failures (e.g. logged out).
    try {
      const token = localStorage.getItem('gm_token');
      if (token) {
        api.put('/users/me', { language: l }).catch(() => {});
      }
    } catch (e) { /* never throw from a setter */ }
  }, []);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  // Once the AuthContext finishes loading and the user lands in the app, make
  // sure the server-side user.language matches the locally chosen language.
  // This catches the case where someone signed up in English and switched to
  // Telugu later — without this sync the AI keeps using their original lang.
  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('gm_token') : null;
    if (!token) return;
    api.get('/auth/me').then(({ data }) => {
      if (data?.language && data.language !== lang) {
        api.put('/users/me', { language: lang }).catch(() => {});
      }
    }).catch(() => {});
  }, [lang]);

  const tr = useCallback((key) => t(lang, key), [lang]);

  const value = useMemo(() => ({ lang, setLang, t: tr }), [lang, setLang, tr]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
